import { useState, useEffect, useMemo } from 'react';
import { subscribeToConversations } from '../services/conversationService';

/**
 * Groups conversations by date: Today, Yesterday, Previous 7 Days, Older.
 */
const groupByDate = (conversations) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups = {
    Today: [],
    Yesterday: [],
    'Previous 7 Days': [],
    Older: [],
  };

  conversations.forEach((conv) => {
    // updatedAt could be a Firestore Timestamp or null (pending server timestamp)
    const ts = conv.updatedAt?.toDate ? conv.updatedAt.toDate() : conv.updatedAt;
    if (!ts) {
      groups.Today.push(conv); // just created, server timestamp pending
      return;
    }

    const date = new Date(ts);
    if (date >= today) {
      groups.Today.push(conv);
    } else if (date >= yesterday) {
      groups.Yesterday.push(conv);
    } else if (date >= weekAgo) {
      groups['Previous 7 Days'].push(conv);
    } else {
      groups.Older.push(conv);
    }
  });

  return groups;
};

/**
 * Real-time subscription to user's conversation list.
 * Returns grouped conversations for the sidebar.
 *
 * @param {object|null} user - Firebase auth user
 * @param {object} opts
 * @param {string|null} opts.assistantType - Filter by assistant, or null for all
 * @param {number} opts.maxResults - Max conversations to fetch
 */
export const useConversationList = (user, { assistantType = null, maxResults = 50 } = {}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setConversations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToConversations(
      user.uid,
      (convs) => {
        setConversations(convs);
        setLoading(false);
      },
      { assistantType, maxResults },
    );

    return () => unsubscribe();
  }, [user, assistantType, maxResults]);

  const grouped = useMemo(() => groupByDate(conversations), [conversations]);

  return { conversations, grouped, loading };
};
